import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Truck, ClipboardCheck, ShieldAlert, Settings as SettingsIcon, Image as ImageIcon } from 'lucide-react';
import { AuthProvider } from './auth/AuthContext';
import { Toaster } from './components/ui/sonner';
import { AppSettingsProvider } from './settings/AppSettingsContext';
import ProtectedRoute from './auth/ProtectedRoute';
import AppLayout from './components/AppLayout';
import RouteFallback from './components/RouteFallback';

// Auth routes kept eager — they gate everything else, we want them instantly available
import LoginPage from './auth/LoginPage';
import AuthCallback from './auth/AuthCallback';

// Everything else lazy-loaded. Reduces initial bundle from ~2MB → ~250kB.
const Dashboard             = lazy(() => import('./Dashboard'));
const ClientsPage           = lazy(() => import('./pages/ClientsPage'));
const TechniciansPage       = lazy(() => import('./pages/TechniciansPage'));
const ChemicalsPage         = lazy(() => import('./pages/ChemicalsPage'));
const OrdersPage            = lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage       = lazy(() => import('./pages/OrderDetailPage'));
const OrderPrintPage        = lazy(() => import('./pages/OrderPrintPage'));
const CESPrintPage          = lazy(() => import('./pages/CESPrintPage'));
const CertificatesPage      = lazy(() => import('./pages/CertificatesPage'));
const AuditPage             = lazy(() => import('./pages/AuditPage'));
const DevicesPage           = lazy(() => import('./pages/DevicesPage'));
const VerifyPage            = lazy(() => import('./pages/VerifyPage'));
const TeamPage              = lazy(() => import('./pages/TeamPage'));
const ProposalsPage         = lazy(() => import('./pages/ProposalsPage'));
const FunnelPage            = lazy(() => import('./pages/FunnelPage'));
const ContractsPage         = lazy(() => import('./pages/ContractsPage'));
const AgendaPage            = lazy(() => import('./pages/AgendaPage'));
const PopsPage              = lazy(() => import('./pages/PopsPage'));
const LaudosPage            = lazy(() => import('./pages/LaudosPage'));
const LaudoTecnicoPage      = lazy(() => import('./pages/LaudoTecnicoPage'));
const LaudoPublicPage       = lazy(() => import('./pages/LaudoPublicPage'));
const RAAEPage              = lazy(() => import('./pages/RAAEPage'));
const FinancePage           = lazy(() => import('./pages/FinancePage'));
const CESPage               = lazy(() => import('./pages/CESPage'));
const CESPublicPage         = lazy(() => import('./pages/CESPublicPage'));
const CompanyProfilePage    = lazy(() => import('./pages/CompanyProfilePage'));
const WhatsAppInboxPage     = lazy(() => import('./pages/WhatsAppInboxPage'));
const WhatsAppSettingsPage  = lazy(() => import('./pages/WhatsAppSettingsPage'));
const NfsePage              = lazy(() => import('./pages/NfsePage'));
const AppSettingsPage       = lazy(() => import('./pages/AppSettingsPage'));
const FieldPage             = lazy(() => import('./pages/FieldPage'));
const CatalogoServicosPage  = lazy(() => import('./pages/CatalogoServicosPage'));
const OperatorRoutinePage   = lazy(() => import('./pages/OperatorRoutinePage'));
const SSTPage               = lazy(() => import('./pages/SSTPage'));
const AgendaBoaIntegrationPage = lazy(() => import('./pages/AgendaBoaIntegrationPage'));
const PlaceholderPage       = lazy(() => import('./pages/PlaceholderPage'));
const RankingPage           = lazy(() => import('./pages/RankingPage'));
const ClientDetailPage      = lazy(() => import('./pages/ClientDetailPage'));
const PublicProposalPage    = lazy(() => import('./pages/PublicProposalPage'));

const P = ({ children }) => (
  <ProtectedRoute>
    <AppLayout>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </AppLayout>
  </ProtectedRoute>
);

// Rota autenticada sem o shell (sidebar/header/menus) — ideal para páginas de impressão
const PrintOnly = ({ children }) => (
  <ProtectedRoute>
    <Suspense fallback={<RouteFallback />}>{children}</Suspense>
  </ProtectedRoute>
);

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verificar/:osId/:token" element={<Suspense fallback={<RouteFallback />}><VerifyPage /></Suspense>} />
      <Route path="/proposta/:token" element={<Suspense fallback={<RouteFallback />}><PublicProposalPage /></Suspense>} />
      <Route path="/ces-publico/:token" element={<Suspense fallback={<RouteFallback />}><CESPublicPage /></Suspense>} />
      <Route path="/laudo-publico/:token" element={<Suspense fallback={<RouteFallback />}><LaudoPublicPage /></Suspense>} />

      {/* Comercial & Vendas */}
      <Route path="/orcamentos" element={<P><ProposalsPage /></P>} />
      <Route path="/funil" element={<P><FunnelPage /></P>} />

      {/* Operações */}
      <Route path="/" element={<P><Dashboard /></P>} />
      <Route path="/agenda" element={<P><AgendaPage /></P>} />
      <Route path="/pops" element={<P><PopsPage /></P>} />
      <Route path="/os" element={<P><OrdersPage /></P>} />
      <Route path="/os/:id" element={<P><OrderDetailPage /></P>} />
      <Route path="/os/:id/impressao" element={<PrintOnly><OrderPrintPage /></PrintOnly>} />
      <Route path="/ces/:id/impressao" element={<PrintOnly><CESPrintPage /></PrintOnly>} />
      <Route path="/laudos/tecnico/:id" element={<PrintOnly><LaudoTecnicoPage /></PrintOnly>} />
      <Route path="/catalogo-servicos" element={<P><CatalogoServicosPage /></P>} />
      <Route path="/rotina" element={<P><OperatorRoutinePage /></P>} />
      <Route path="/ranking" element={<P><RankingPage /></P>} />
      <Route path="/telemetria" element={<P><PlaceholderPage icon={Truck} title="Telemetria & Rotas de Campo" subtitle="Rastreamento GPS de veículos e rotas em tempo real" /></P>} />

      {/* Cadastros */}
      <Route path="/clientes" element={<P><ClientsPage /></P>} />
      <Route path="/clientes/:id" element={<P><ClientDetailPage /></P>} />
      <Route path="/contratos" element={<P><ContractsPage /></P>} />
      <Route path="/produtos" element={<P><ChemicalsPage /></P>} />
      <Route path="/galeria" element={<P><PlaceholderPage icon={ImageIcon} title="Galeria & Links de Imagens HTML" subtitle="Banco de imagens para envio ao cliente e marketing" /></P>} />

      {/* Administração */}
      <Route path="/nfse" element={<P><NfsePage /></P>} />
      <Route path="/financeiro" element={<P><FinancePage /></P>} />
      <Route path="/campo" element={<P><FieldPage /></P>} />
      <Route path="/whatsapp/inbox" element={<P><WhatsAppInboxPage /></P>} />
      <Route path="/whatsapp/chaves" element={<P><WhatsAppSettingsPage /></P>} />
      <Route path="/equipe" element={<P><TeamPage /></P>} />
      <Route path="/tecnicos" element={<P><TechniciansPage /></P>} />
      <Route path="/certificados" element={<P><CertificatesPage /></P>} />
      <Route path="/ces" element={<P><CESPage /></P>} />
      <Route path="/ces/:id" element={<P><CESPage /></P>} />
      <Route path="/laudos" element={<P><LaudosPage /></P>} />
      <Route path="/raae" element={<P><RAAEPage /></P>} />

      {/* Sistema */}
      <Route path="/sst" element={<P><SSTPage /></P>} />
      <Route path="/integracoes/agenda-boa" element={<P><AgendaBoaIntegrationPage /></P>} />
      <Route path="/dispositivos" element={<P><DevicesPage /></P>} />
      <Route path="/auditoria" element={<P><AuditPage /></P>} />
      <Route path="/configuracoes" element={<P><AppSettingsPage /></P>} />
      <Route path="/perfil-empresa" element={<P><CompanyProfilePage /></P>} />

      <Route path="*" element={<P><Dashboard /></P>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppSettingsProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AppSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
